import SortableDashboardTabs from '../components/navigation/SortableDashboardTabs';
import { runGuardedDashboardNavigation } from '../components/routing/navigationConfirmation';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useJobsOptional } from '../contexts/JobsContext';
import { useDashboardRoutePanelActive } from '../contexts/DashboardRoutePanelContext';
import {
  createDashboardTab,
  normalizeDashboardTabsState,
  readDashboardTabsState,
  writeDashboardTabsState,
} from '../utils/dashboardTabs';
import {
  getDashboardNavDestinationLabel,
  getDashboardNavPathname,
  getDashboardNavRouteMeta,
  getDashboardNavSection,
} from '../utils/dashboardNavRouteMeta';
import {
  BellIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Cog6ToothIcon as Cog6ToothSolidIcon } from '@heroicons/react/24/solid';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { JobsPanel } from '../components/panels/JobsPanel';
import { useCompactAssistantViewport } from '../components/panels/DashboardAssistantChatDock/hooks/useAssistantSurfaces';
import DashboardPaymentNotices from './onboarding/DashboardPaymentNotices';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'charitystack-dashboard-sidebar-collapsed';
const SIDEBAR_WIDTH_STORAGE_KEY = 'charitystack-dashboard-sidebar-width';
const SIDEBAR_STATE_EVENT = 'charitystack-dashboard-sidebar-state-change';
// Keep these constants in lock-step with packages/dashboard/src/partials/Sidebar.js.
// The two files own independent state synced via SIDEBAR_STATE_EVENT and
// localStorage, so the width domains must match.
const SIDEBAR_COLLAPSED_WIDTH = 80;
const SIDEBAR_DEFAULT_WIDTH = 224;
const SIDEBAR_EXPANDED_MIN_WIDTH = 200;
const DASHBOARD_NAV_STATE_STORAGE_KEY = 'charitystack-dashboard-nav-state';
const DASHBOARD_PENDING_NAV_STORAGE_KEY = 'charitystack-dashboard-pending-nav';
const DASHBOARD_RECENT_NAV_STORAGE_KEY = 'charitystack-dashboard-recent-nav';
const MAX_DASHBOARD_NAV_ENTRIES = 50;
const MAX_DASHBOARD_RECENT_NAV_ENTRIES = 8;

const getInitialSidebarCollapsed = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
};

const getExpandedSidebarWidth = () => {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  const storedWidth = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
  // Reject any stored width below the expanded minimum — those values are
  // either stale (from before the invariant was enforced) or corrupted and
  // would produce a half-collapsed sidebar with clipped labels.
  return Number.isFinite(storedWidth) && storedWidth >= SIDEBAR_EXPANDED_MIN_WIDTH
    ? storedWidth
    : SIDEBAR_DEFAULT_WIDTH;
};

const getInitialDashboardNavState = () => ({
  entries: [],
  index: -1,
});

const normalizeDashboardNavState = (state) => {
  if (!state || !Array.isArray(state.entries) || typeof state.index !== 'number') {
    return getInitialDashboardNavState();
  }

  const entries = state.entries.filter((entry) => typeof entry === 'string' && entry);
  if (!entries.length) return getInitialDashboardNavState();

  return {
    entries,
    index: Math.min(entries.length - 1, Math.max(0, state.index)),
  };
};

const readDashboardNavState = () => {
  if (typeof window === 'undefined') return getInitialDashboardNavState();
  try {
    return normalizeDashboardNavState(
      JSON.parse(sessionStorage.getItem(DASHBOARD_NAV_STATE_STORAGE_KEY) || 'null'),
    );
  } catch (_) {
    return getInitialDashboardNavState();
  }
};

const writeDashboardNavState = (state) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DASHBOARD_NAV_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (_) {
    /* storage may be unavailable */
  }
};

const normalizeDashboardRecentNavPaths = (paths) => {
  if (!Array.isArray(paths)) return [];

  const seenPaths = new Set();
  return paths
    .map((entry) => (typeof entry === 'string' ? entry : entry?.path))
    .filter((path) => typeof path === 'string' && path)
    .filter((path) => {
      if (seenPaths.has(path)) return false;
      seenPaths.add(path);
      return true;
    })
    .slice(0, MAX_DASHBOARD_NAV_ENTRIES);
};

const readDashboardRecentNavPaths = () => {
  if (typeof window === 'undefined') return [];
  try {
    return normalizeDashboardRecentNavPaths(
      JSON.parse(sessionStorage.getItem(DASHBOARD_RECENT_NAV_STORAGE_KEY) || '[]'),
    );
  } catch (_) {
    return [];
  }
};

const writeDashboardRecentNavPaths = (paths) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      DASHBOARD_RECENT_NAV_STORAGE_KEY,
      JSON.stringify(normalizeDashboardRecentNavPaths(paths)),
    );
  } catch (_) {
    /* storage may be unavailable */
  }
};

const recordDashboardRecentNavPath = (path) => {
  if (!path) return readDashboardRecentNavPaths();

  const nextPaths = normalizeDashboardRecentNavPaths([
    path,
    ...readDashboardRecentNavPaths().filter((recentPath) => recentPath !== path),
  ]);
  writeDashboardRecentNavPaths(nextPaths);
  return nextPaths;
};

const getDashboardPathFromHref = (href) => {
  if (typeof window === 'undefined' || !href) return null;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;

    if (url.pathname === '/dashboard') {
      return `/${url.search}${url.hash}`;
    }

    const pathname = url.pathname.startsWith('/dashboard/')
      ? url.pathname.slice('/dashboard'.length)
      : url.pathname;

    return `${pathname || '/'}${url.search}${url.hash}`;
  } catch (_) {
    return null;
  }
};

const getClosestDashboardNavIndex = (entries, currentIndex, path) => {
  if (!Array.isArray(entries) || !path) return -1;

  return entries.reduce((closestIndex, entry, index) => {
    if (entry !== path) return closestIndex;
    if (closestIndex < 0) return index;

    const currentDistance = Math.abs(index - currentIndex);
    const closestDistance = Math.abs(closestIndex - currentIndex);
    return currentDistance < closestDistance ? index : closestIndex;
  }, -1);
};

const readPendingDashboardNav = () => {
  if (typeof window === 'undefined') return null;
  try {
    const pending = JSON.parse(sessionStorage.getItem(DASHBOARD_PENDING_NAV_STORAGE_KEY) || 'null');
    if (pending && typeof pending.index === 'number' && typeof pending.path === 'string') {
      return pending;
    }
  } catch (_) {
    /* storage may be unavailable */
  }
  return null;
};

const writePendingDashboardNav = (pending) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DASHBOARD_PENDING_NAV_STORAGE_KEY, JSON.stringify(pending));
  } catch (_) {
    /* storage may be unavailable */
  }
};

const clearPendingDashboardNav = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(DASHBOARD_PENDING_NAV_STORAGE_KEY);
  } catch (_) {
    /* storage may be unavailable */
  }
};

const ToolbarTooltip = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent
      side="bottom"
      aria-label={label}
      data-label={label}
      className="before:content-[attr(data-label)]"
    />
  </Tooltip>
);

const isTextEntryTarget = (target) => {
  if (!target || typeof target.closest !== 'function') return false;

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]',
    ),
  );
};

const DashboardNavIcon = ({ path, className = 'h-5 w-5' }) => {
  const section = getDashboardNavSection(path);
  const isSettingsPage =
    section === 'settings' && getDashboardNavPathname(path) !== '/settings/imports/new';
  const commonProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    className,
  };

  if (isSettingsPage) {
    return <Cog6ToothSolidIcon className={className} aria-hidden="true" />;
  }

  switch (section) {
    case '':
      return (
        <svg {...commonProps}>
          <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
          <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
        </svg>
      );
    case 'contacts':
      return (
        <svg {...commonProps}>
          <path
            fillRule="evenodd"
            d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'forms':
      return (
        <svg {...commonProps}>
          <path d="M5.625 3.75a2.625 2.625 0 1 0 0 5.25h12.75a2.625 2.625 0 0 0 0-5.25H5.625ZM3.75 11.25a.75.75 0 0 0 0 1.5h16.5a.75.75 0 0 0 0-1.5H3.75ZM3 15.75a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75ZM3.75 18.75a.75.75 0 0 0 0 1.5h16.5a.75.75 0 0 0 0-1.5H3.75Z" />
        </svg>
      );
    case 'elements':
      return (
        <svg {...commonProps}>
          <path d="M6 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6ZM15.75 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3H18a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-2.25ZM6 12.75a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3v-2.25a3 3 0 0 0-3-3H6ZM17.625 13.5a.75.75 0 0 0-1.5 0v2.625H13.5a.75.75 0 0 0 0 1.5h2.625v2.625a.75.75 0 0 0 1.5 0v-2.625h2.625a.75.75 0 0 0 0-1.5h-2.625V13.5Z" />
        </svg>
      );
    case 'payments':
      return (
        <svg {...commonProps}>
          <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
          <path
            fillRule="evenodd"
            d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'subscriptions':
      return (
        <svg {...commonProps}>
          <path
            fillRule="evenodd"
            d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'payouts':
      return (
        <svg {...commonProps}>
          <path
            fillRule="evenodd"
            d="M16.5 3.75a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5V15a.75.75 0 0 0-1.5 0v3.75a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5.25a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3V9A.75.75 0 1 0 9 9V5.25a1.5 1.5 0 0 1 1.5-1.5h6Zm-5.03 4.72a.75.75 0 0 0 0 1.06l1.72 1.72H2.25a.75.75 0 0 0 0 1.5h10.94l-1.72 1.72a.75.75 0 1 0 1.06 1.06l3-3a.75.75 0 0 0 0-1.06l-3-3a.75.75 0 0 0-1.06 0Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'broadcasts':
      return (
        <svg {...commonProps}>
          <path
            fillRule="evenodd"
            d="M16.881 4.346A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.6.772-2.546a20.092 20.092 0 0 1-.722-2.058 23.24 23.24 0 0 0 5.046 1.146 4.5 4.5 0 0 0 4.625-4.62v-5.04a4.5 4.5 0 0 0-4.119-4.482Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'segments':
      return (
        <svg {...commonProps}>
          <path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
        </svg>
      );
    // 'email-blasts' + 'email-templates' icon cases removed (PE-1671).
    default:
      return (
        <svg {...commonProps}>
          <path d="M3 6a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6ZM12.75 6a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3V6ZM3 15.75a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-2.25ZM12.75 15.75a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3v-2.25Z" />
        </svg>
      );
  }
};

const getDashboardRecentNavEntries = (recentPaths, currentPath) => {
  const orderedPaths = normalizeDashboardRecentNavPaths([
    currentPath,
    ...normalizeDashboardRecentNavPaths(recentPaths),
  ]).slice(0, MAX_DASHBOARD_RECENT_NAV_ENTRIES);

  return orderedPaths.map((path) => ({
    path,
    isCurrent: path === currentPath,
  }));
};

function Header({
  selectableFunds,
  selectableForms,
  merchant,
  grabSelectableFunds,
  groups,
  onContactCreated,
  onTransactionCreated,
  onFormCreated,
}) {
  const jobsContext = useJobsOptional();
  const isCompact = useCompactAssistantViewport();
  const routePanelActive = useDashboardRoutePanelActive();
  const history = useHistory();
  const location = useLocation();
  const currentDashboardPath = `${location.pathname}${location.search}${location.hash}`;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [dashboardNavState, setDashboardNavState] = useState(readDashboardNavState);
  const [dashboardRecentNavPaths, setDashboardRecentNavPaths] = useState(
    readDashboardRecentNavPaths,
  );
  const [dashboardTabsState, setDashboardTabsState] = useState(() =>
    readDashboardTabsState(currentDashboardPath),
  );

  // Bell animation on notification count increase (skip initial mount)
  const prevBellCountRef = useRef(0);
  const isFirstBellEffect = useRef(true);
  const [bellAnimating, setBellAnimating] = useState(false);

  const bellCount = jobsContext
    ? jobsContext.activeCount + jobsContext.unreadCount + jobsContext.transitioningCount
    : 0;

  useEffect(() => {
    if (isFirstBellEffect.current) {
      isFirstBellEffect.current = false;
      prevBellCountRef.current = bellCount;
      return;
    }
    if (bellCount > prevBellCountRef.current) {
      setBellAnimating(true);
      const timer = setTimeout(() => setBellAnimating(false), 1000);
      prevBellCountRef.current = bellCount;
      return () => clearTimeout(timer);
    }
    prevBellCountRef.current = bellCount;
  }, [bellCount]);

  useEffect(() => {
    if (!routePanelActive) return undefined;

    const handleSidebarStateChange = (event) => {
      if (typeof event.detail?.collapsed !== 'boolean') return;
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener(SIDEBAR_STATE_EVENT, handleSidebarStateChange);
    return () => window.removeEventListener(SIDEBAR_STATE_EVENT, handleSidebarStateChange);
  }, [routePanelActive]);

  const toggleDesktopSidebar = () => {
    const collapsed = !sidebarCollapsed;
    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : getExpandedSidebarWidth();
    setSidebarCollapsed(collapsed);

    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
    } catch (_) {
      /* storage may be unavailable */
    }

    window.dispatchEvent(
      new CustomEvent(SIDEBAR_STATE_EVENT, {
        detail: {
          collapsed,
          width,
        },
      }),
    );
  };

  useEffect(() => {
    if (!routePanelActive) return undefined;

    const storedState = readDashboardNavState();
    const pendingNav = readPendingDashboardNav();
    const normalizedTabsState = readDashboardTabsState(currentDashboardPath);
    const nextTabsState = {
      ...normalizedTabsState,
      tabs: normalizedTabsState.tabs.map((tab) =>
        tab.id === normalizedTabsState.activeTabId
          ? {
              ...tab,
              path: currentDashboardPath,
            }
          : tab,
      ),
    };
    setDashboardRecentNavPaths(recordDashboardRecentNavPath(currentDashboardPath));
    setDashboardTabsState(nextTabsState);
    writeDashboardTabsState(nextTabsState);

    if (
      pendingNav &&
      pendingNav.path === currentDashboardPath &&
      storedState.entries[pendingNav.index] === currentDashboardPath
    ) {
      const nextState = {
        entries: storedState.entries,
        index: pendingNav.index,
      };
      clearPendingDashboardNav();
      writeDashboardNavState(nextState);
      setDashboardNavState(nextState);
      return;
    }

    clearPendingDashboardNav();

    if (history.action === 'POP') {
      const popNavIndex = getClosestDashboardNavIndex(
        storedState.entries,
        storedState.index,
        currentDashboardPath,
      );

      if (popNavIndex >= 0) {
        const nextState = {
          entries: storedState.entries,
          index: popNavIndex,
        };
        writeDashboardNavState(nextState);
        setDashboardNavState(nextState);
        return;
      }
    }

    if (storedState.entries[storedState.index] === currentDashboardPath) {
      setDashboardNavState(storedState);
      return;
    }

    let entries = storedState.entries.slice(0, storedState.index + 1);
    if (entries[entries.length - 1] !== currentDashboardPath) {
      entries.push(currentDashboardPath);
    }

    if (entries.length > MAX_DASHBOARD_NAV_ENTRIES) {
      entries = entries.slice(entries.length - MAX_DASHBOARD_NAV_ENTRIES);
    }

    const nextState = {
      entries,
      index: entries.length - 1,
    };
    writeDashboardNavState(nextState);
    setDashboardNavState(nextState);
  }, [currentDashboardPath, history.action, routePanelActive]);

  const handleDashboardBack = () => {
    if (dashboardNavState.index <= 0) return;
    const nextIndex = dashboardNavState.index - 1;
    const nextPath = dashboardNavState.entries[nextIndex];
    writePendingDashboardNav({
      index: nextIndex,
      path: nextPath,
    });
    history.push(nextPath);
  };

  const handleDashboardForward = () => {
    if (dashboardNavState.index >= dashboardNavState.entries.length - 1) return;
    const nextIndex = dashboardNavState.index + 1;
    const nextPath = dashboardNavState.entries[nextIndex];
    writePendingDashboardNav({
      index: nextIndex,
      path: nextPath,
    });
    history.push(nextPath);
  };

  const handleDashboardHistorySelect = (entry) => {
    if (!entry?.path || entry.path === currentDashboardPath) return;
    history.push(entry.path);
  };

  const handleDashboardTabSelect = useCallback(
    (tab) => {
      if (!tab?.id || tab.id === dashboardTabsState.activeTabId) return;

      runGuardedDashboardNavigation(history, () => {
      const nextTabsState = {
        ...dashboardTabsState,
        activeTabId: tab.id,
      };
      writeDashboardTabsState(nextTabsState);
      setDashboardTabsState(nextTabsState);

      if (tab.path !== currentDashboardPath) {
        history.push(tab.path);
      }
      });
    },
    [currentDashboardPath, dashboardTabsState, history],
  );

  const openDashboardPathInNewTab = useCallback(
    (path = '/') => {
      runGuardedDashboardNavigation(history, () => {
      const nextTab = createDashboardTab(path);
      const nextTabsState = normalizeDashboardTabsState(
        {
          tabs: [...dashboardTabsState.tabs, nextTab],
          activeTabId: nextTab.id,
        },
        path,
      );
      writeDashboardTabsState(nextTabsState);
      setDashboardTabsState(nextTabsState);

      if (currentDashboardPath !== path) {
        history.push(path);
      }
      });
    },
    [currentDashboardPath, dashboardTabsState.tabs, history],
  );

  const handleCreateDashboardTab = () => {
    openDashboardPathInNewTab('/');
  };

  const handleCloseDashboardTab = useCallback(
    (tabToClose) => {
      if (!tabToClose?.id) return;

      const tabIndex = dashboardTabsState.tabs.findIndex((tab) => tab.id === tabToClose.id);
      if (tabIndex < 0) return;

      const commitClose = () => {
      const remainingTabs = dashboardTabsState.tabs.filter((tab) => tab.id !== tabToClose.id);
      const fallbackTab = createDashboardTab('/');

      if (!remainingTabs.length) {
        const nextTabsState = {
          tabs: [fallbackTab],
          activeTabId: fallbackTab.id,
        };
        writeDashboardTabsState(nextTabsState);
        setDashboardTabsState(nextTabsState);
        if (currentDashboardPath !== '/') history.push('/');
        return;
      }

      const nextActiveTab =
        tabToClose.id === dashboardTabsState.activeTabId
          ? remainingTabs[Math.min(tabIndex, remainingTabs.length - 1)]
          : remainingTabs.find((tab) => tab.id === dashboardTabsState.activeTabId) ||
            remainingTabs[remainingTabs.length - 1];

      const nextTabsState = {
        tabs: remainingTabs,
        activeTabId: nextActiveTab.id,
      };
      writeDashboardTabsState(nextTabsState);
      setDashboardTabsState(nextTabsState);

      if (
        tabToClose.id === dashboardTabsState.activeTabId &&
        nextActiveTab.path !== currentDashboardPath
      ) {
        history.push(nextActiveTab.path);
      }
      };
      if (tabToClose.id === dashboardTabsState.activeTabId) runGuardedDashboardNavigation(history, commitClose);
      else commitClose();
    },
    [currentDashboardPath, dashboardTabsState, history],
  );

  const handleSelectAdjacentDashboardTab = useCallback(
    (direction) => {
      if (dashboardTabsState.tabs.length <= 1) return;

      const activeIndex = dashboardTabsState.tabs.findIndex(
        (tab) => tab.id === dashboardTabsState.activeTabId,
      );
      if (activeIndex < 0) return;

      const nextIndex =
        direction === 'previous'
          ? (activeIndex - 1 + dashboardTabsState.tabs.length) % dashboardTabsState.tabs.length
          : (activeIndex + 1) % dashboardTabsState.tabs.length;
      handleDashboardTabSelect(dashboardTabsState.tabs[nextIndex]);
    },
    [dashboardTabsState, handleDashboardTabSelect],
  );

  const handleReorderDashboardTab = useCallback(
    (sourceTabId, targetTabId) => {
      if (!sourceTabId || !targetTabId || sourceTabId === targetTabId) return;

      const sourceIndex = dashboardTabsState.tabs.findIndex((tab) => tab.id === sourceTabId);
      const targetIndex = dashboardTabsState.tabs.findIndex((tab) => tab.id === targetTabId);
      if (sourceIndex < 0 || targetIndex < 0) return;

      const nextTabs = [...dashboardTabsState.tabs];
      const [movedTab] = nextTabs.splice(sourceIndex, 1);
      nextTabs.splice(targetIndex, 0, movedTab);

      const nextTabsState = {
        tabs: nextTabs,
        activeTabId: dashboardTabsState.activeTabId,
      };
      writeDashboardTabsState(nextTabsState);
      setDashboardTabsState(nextTabsState);
    },
    [dashboardTabsState],
  );

  useEffect(() => {
    if (!routePanelActive) return undefined;

    const handleDashboardTabShortcut = (event) => {
      if (event.defaultPrevented || !event.key) return;

      const hasPrimaryModifier = event.metaKey || event.ctrlKey;
      if (!hasPrimaryModifier || event.altKey) return;

      const key = event.key.toLowerCase();
      const activeTab = dashboardTabsState.tabs.find(
        (tab) => tab.id === dashboardTabsState.activeTabId,
      );

      if (key === 't') {
        event.preventDefault();
        openDashboardPathInNewTab('/');
        return;
      }

      if (key === 'w') {
        event.preventDefault();
        if (activeTab) handleCloseDashboardTab(activeTab);
        return;
      }

      if (event.shiftKey && key === '[') {
        event.preventDefault();
        handleSelectAdjacentDashboardTab('previous');
        return;
      }

      if (event.shiftKey && key === ']') {
        event.preventDefault();
        handleSelectAdjacentDashboardTab('next');
        return;
      }

      if (!event.shiftKey && /^[1-8]$/.test(event.key)) {
        const tab = dashboardTabsState.tabs[Number(event.key) - 1];
        if (!tab) return;
        event.preventDefault();
        handleDashboardTabSelect(tab);
      }
    };

    window.addEventListener('keydown', handleDashboardTabShortcut);
    return () => window.removeEventListener('keydown', handleDashboardTabShortcut);
  }, [
    dashboardTabsState,
    handleCloseDashboardTab,
    handleDashboardTabSelect,
    handleSelectAdjacentDashboardTab,
    openDashboardPathInNewTab,
    routePanelActive,
  ]);

  useEffect(() => {
    if (!routePanelActive) return undefined;

    const handleDashboardLinkOpenInTab = (event) => {
      if (event.defaultPrevented || event.altKey || event.shiftKey) return;
      if (isTextEntryTarget(event.target)) return;

      const isModifierClick =
        event.type === 'click' && event.button === 0 && (event.metaKey || event.ctrlKey);
      const isMiddleClick = event.type === 'auxclick' && event.button === 1;
      if (!isModifierClick && !isMiddleClick) return;

      const anchor =
        event.target && typeof event.target.closest === 'function'
          ? event.target.closest('a[href]')
          : null;
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;

      const tabPath = getDashboardPathFromHref(anchor.getAttribute('href'));
      if (!tabPath) return;

      event.preventDefault();
      event.stopPropagation();
      openDashboardPathInNewTab(tabPath);
    };

    document.addEventListener('click', handleDashboardLinkOpenInTab, true);
    document.addEventListener('auxclick', handleDashboardLinkOpenInTab, true);
    return () => {
      document.removeEventListener('click', handleDashboardLinkOpenInTab, true);
      document.removeEventListener('auxclick', handleDashboardLinkOpenInTab, true);
    };
  }, [openDashboardPathInNewTab, routePanelActive]);

  const canGoBack = dashboardNavState.index > 0;
  const canGoForward = dashboardNavState.index < dashboardNavState.entries.length - 1;
  const backDestination = canGoBack ? dashboardNavState.entries[dashboardNavState.index - 1] : null;
  const forwardDestination = canGoForward
    ? dashboardNavState.entries[dashboardNavState.index + 1]
    : null;
  const backLabel = backDestination
    ? `Back to ${getDashboardNavDestinationLabel(backDestination)}`
    : 'No dashboard history';
  const forwardLabel = forwardDestination
    ? `Forward to ${getDashboardNavDestinationLabel(forwardDestination)}`
    : 'No forward history';
  const recentNavEntries = getDashboardRecentNavEntries(
    dashboardRecentNavPaths,
    currentDashboardPath,
  );


  if (!routePanelActive) return null;

  return (

    <header className="mb-1.5 w-full bg-transparent">
      <TooltipProvider delayDuration={150}>
        <div className={`hidden lg:flex h-11 w-full min-w-0 items-center gap-2 bg-paper-muted py-1.5 pl-4 pr-4 lg:pl-0`}>
          <div
            className="hidden h-8 flex-shrink-0 items-center gap-0.5 rounded-md border border-edge bg-white px-1 shadow-sm shadow-black/[0.02] lg:flex"
            data-dashboard-header-actions
          >
            <div className="flex flex-shrink-0 items-center gap-0.5">
              <ToolbarTooltip label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                <button
                  className="hidden h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-ink-muted transition duration-150 hover:bg-paper-inset hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0 lg:flex"
                  onClick={toggleDesktopSidebar}
                  aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {sidebarCollapsed ? (
                    <PanelLeftOpen className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </button>
              </ToolbarTooltip>
            </div>
            <div
              className="flex h-full items-center gap-0.5"
            >
              <ToolbarTooltip label={backLabel}>
                <span>
                  <button
                    className={`flex h-7 w-7 items-center justify-center rounded-md transition duration-150 focus:outline-none focus:ring-0 focus-visible:ring-0 ${
                      canGoBack
                        ? 'text-ink-muted hover:bg-paper-inset hover:text-ink'
                        : 'cursor-default text-ink-muted/20 opacity-60'
                    }`}
                    onClick={handleDashboardBack}
                    disabled={!canGoBack}
                    aria-label={backLabel}
                  >
                    <ChevronLeftIcon className="h-4 w-4" strokeWidth={2} />
                  </button>
                </span>
              </ToolbarTooltip>
              <ToolbarTooltip label={forwardLabel}>
                <span>
                  <button
                    className={`flex h-7 w-7 items-center justify-center rounded-md transition duration-150 focus:outline-none focus:ring-0 focus-visible:ring-0 ${
                      canGoForward
                        ? 'text-ink-muted hover:bg-paper-inset hover:text-ink'
                        : 'cursor-default text-ink-muted/20 opacity-60'
                    }`}
                    onClick={handleDashboardForward}
                    disabled={!canGoForward}
                    aria-label={forwardLabel}
                  >
                    <ChevronRightIcon className="h-4 w-4" strokeWidth={2} />
                  </button>
                </span>
              </ToolbarTooltip>
              <DropdownMenu>
                <ToolbarTooltip label="Page history">
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition duration-150 hover:bg-paper-inset hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0"
                      aria-label="Page history"
                    >
                      <ClockIcon className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </DropdownMenuTrigger>
                </ToolbarTooltip>
                <DropdownMenuContent
                  align="start"
                  side="bottom"
                  sideOffset={8}
                  className="w-[min(18rem,calc(100vw-2rem))] rounded-lg p-1.5 shadow-lg"
                >
                  <div className="px-2.5 pb-1.5 pt-1 text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Recently viewed
                  </div>
                  <div className="max-h-[min(70vh,24rem)] overflow-y-auto">
                    {recentNavEntries.map((entry) => {
                      const navMeta = getDashboardNavRouteMeta(entry.path);

                      return (
                        <DropdownMenuItem
                          key={entry.path}
                          className={`flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-paper-inset focus:bg-paper-inset ${
                            entry.isCurrent ? 'bg-paper-muted' : ''
                          }`}
                          aria-current={entry.isCurrent ? 'page' : undefined}
                          onClick={() => handleDashboardHistorySelect(entry)}
                        >
                          <span className={entry.isCurrent ? 'text-ink' : 'text-ink-secondary'}>
                            <DashboardNavIcon path={entry.path} className="h-5 w-5" />
                          </span>
                          <span
                            className={`min-w-0 flex-1 truncate text-[15px] font-medium ${
                              entry.isCurrent ? 'text-ink' : 'text-ink-secondary'
                            }`}
                          >
                            {navMeta.label}
                          </span>
                          {navMeta.badge && (
                            <span className="ml-1 max-w-[5.5rem] flex-shrink-0 truncate rounded bg-paper-inset px-1.5 py-0.5 text-[11px] font-medium text-ink-muted">
                              {navMeta.badge}
                            </span>
                          )}
                          {entry.isCurrent && (
                            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-sage" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Notifications Bell */}
              {jobsContext && !isCompact && (
                <JobsPanel>
                  <button
                    className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition duration-150 hover:bg-paper-inset focus:outline-none focus:ring-0 focus-visible:ring-0 ${bellCount > 0 ? 'text-sage hover:text-sage-hover' : 'text-ink-muted hover:text-ink'}`}
                    aria-label="Notifications"
                    aria-haspopup="dialog"
                  >
                    <BellIcon className="h-5 w-5" aria-hidden="true" />
                    {bellCount > 0 && (
                      <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-sage text-[10px] font-medium text-white">
                        {bellAnimating && (
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-75" />
                        )}
                        {bellCount}
                      </span>
                    )}
                  </button>
                </JobsPanel>
              )}
            </div>
          </div>

          <SortableDashboardTabs
            tabs={dashboardTabsState.tabs}
            onReorder={handleReorderDashboardTab}
            getTabClassName={(tab) => tab.id === dashboardTabsState.activeTabId
              ? 'border-edge bg-white text-ink shadow-sm shadow-black/[0.04]'
              : 'border-transparent bg-transparent text-ink-muted hover:border-edge-input/70 hover:bg-white/70 hover:text-ink'}
            renderTab={(tab, { handleProps, overlay }) => {
              const navMeta = getDashboardNavRouteMeta(tab.path);
              const isActive = tab.id === dashboardTabsState.activeTabId;
              return <>
                  <button
                    {...handleProps}
                    type="button"
                    role={overlay ? undefined : 'tab'}
                    aria-selected={isActive}
                    title={navMeta.label}
                    className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-l-lg px-3 text-left focus:outline-none focus:ring-0 focus-visible:ring-0"
                    onClick={overlay ? undefined : () => handleDashboardTabSelect(tab)}
                  >
                    <span className={isActive ? 'text-ink' : 'text-ink-muted'}>
                      <DashboardNavIcon path={tab.path} className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium">{navMeta.label}</span>
                    {navMeta.badge && (
                      <span className="max-w-[4.5rem] flex-shrink-0 truncate rounded bg-paper-inset px-1.5 py-0.5 text-[11px] font-medium text-ink-muted">
                        {navMeta.badge}
                      </span>
                    )}
                  </button>
                  {overlay && dashboardTabsState.tabs.length > 1 && <span className="mr-1 h-5 w-5 shrink-0" />}
                  {!overlay && dashboardTabsState.tabs.length > 1 && (
                    <ToolbarTooltip label={`Close ${navMeta.label}`}>
                      <button
                        type="button"
                        className={`mr-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-ink-muted transition duration-150 hover:bg-paper-inset hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0 ${
                          isActive ? 'opacity-70' : 'opacity-0 group-hover:opacity-70'
                        }`}
                        aria-label={`Close ${navMeta.label} tab`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCloseDashboardTab(tab);
                        }}
                      >
                        <XMarkIcon className="h-3.5 w-3.5" strokeWidth={2} />
                      </button>
                    </ToolbarTooltip>
                  )}
              </>;
            }}
          >
            <ToolbarTooltip label="New tab">
              <button
                type="button"
                className="ml-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-ink-muted transition duration-150 hover:bg-white/70 hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0"
                aria-label="New tab"
                onClick={handleCreateDashboardTab}
              >
                <PlusIcon className="h-4 w-4" strokeWidth={2} />
              </button>
            </ToolbarTooltip>
          </SortableDashboardTabs>

        </div>
      </TooltipProvider>
      <DashboardPaymentNotices />
    </header>
  );
}

export default Header;
